import { useReactiveVar } from '@apollo/client'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import type { AllowDropOptions } from 'rc-tree/es/Tree'
import type { BasicDataNode, Key } from 'rc-tree/es/interface'
import React, { useCallback } from 'react'
import { useDragAndDropActions, useIsDragging } from '../DragAndDropStore'
import {
    useHoveredItemActions,
    useSelectedMainMenuNode,
} from '../HoveredItemStore'
import { useSelectedKey } from '../NodeKeySelectionStore'
import { useShallowSelector } from '../Workspace2Store'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from '../features/keyOperations/store/NodeMapStore'
import { useMOPTDragAndDropValidation } from '../features/moptArticleRestriction'
import { useHoveredActionMutationService } from '../services/hoveredActionMutationService'
import { useTransactionManager } from '../services/transactionManager'
import { ContentItemViewNodesType } from '../types'
import { DragAndDropInfo } from '../types/dragDropInfo.type'
import {
    getAllDescendantsOptimized,
    getDragAndDropNodes,
    getSortedSiblings,
    reorderSiblingContents,
    replaceNodesById,
} from '../utils'
import { tryGetShortcutKey } from '../utils/shortcutKeyUtils'
import { useEditingPermissions } from './useEditingPermissions'

/** Default drop position (1 = after target) when not specified. */
const DEFAULT_DROP_POSITION = 1

/** Tree node type with key for allowDrop functionality */
type TreeNodeWithKey = BasicDataNode & { key: Key }

/** Checks if drop node is the parent of drag node. */
const isParentNode = (
    dropNode: Partial<ContentItemViewNodesType> | undefined,
    dragNode: Partial<ContentItemViewNodesType>
): boolean =>
    dropNode?.id === dragNode.dtvParentId ||
    dropNode?.id === dragNode.dtvContentItemParentId

/** Checks if node is a NUGGET boundary placeholder. */
const isBoundaryNode = (
    node: Partial<ContentItemViewNodesType> | undefined
): boolean => {
    return node?.type === 'NUGGET' || node?.partType === 'NUGGET'
}

/**
 * Validation result for drop operations
 */
type DropValidationResult = {
    isValid: boolean
    reason?: 'TEXT_TYPE_MISMATCH' | 'PARENT_MISMATCH'
}

/**
 * Validates drop based on parent relationships and textType.
 * Allows: parent nodes, NUGGET boundaries, or matching textType with shared parent.
 * Rejects only if BOTH parent IDs differ (AND logic).
 */
const isValidDrop = (
    dropNode: Partial<ContentItemViewNodesType> | undefined,
    dragNode: Partial<ContentItemViewNodesType>
): DropValidationResult => {
    // Exception: Allow if dropNode IS the parent or a NUGGET boundary
    if (isParentNode(dropNode, dragNode) || isBoundaryNode(dropNode)) {
        return { isValid: true }
    }

    // Check textType match
    if (dragNode.textType !== dropNode?.textType) {
        return { isValid: false, reason: 'TEXT_TYPE_MISMATCH' }
    }

    // Check parent relationships - both must differ to reject
    const differentParent = dragNode.dtvParentId !== dropNode?.dtvParentId
    const differentContentParent =
        dragNode.dtvContentItemParentId !== dropNode?.dtvContentItemParentId

    const parentMismatch = differentParent && differentContentParent
    return {
        isValid: !parentMismatch,
        reason: parentMismatch ? 'PARENT_MISMATCH' : undefined,
    }
}

/**
 * Resolves drop index for boundary nodes (parent or NUGGET).
 * Returns 0 for top position, siblings.length - 1 for bottom.
 */
const resolveDropIndexForBoundaryNodes = (
    dropNode: Partial<ContentItemViewNodesType> | undefined,
    siblings: Partial<ContentItemViewNodesType>[],
    visualDropPosition: number
): number => {
    const topIndex = 0
    const bottomIndex = siblings.length - 1
    const fallback = visualDropPosition < 0 ? topIndex : bottomIndex

    if (siblings.length === 0 || dropNode?.order === undefined) return fallback

    const firstOrder = siblings[0]?.order
    const lastOrder = siblings[bottomIndex]?.order
    if (firstOrder === undefined || lastOrder === undefined) return fallback

    const { order, textType } = dropNode

    // Handle Article nodes separately
    if (textType === 'Article') {
        return order <= firstOrder || visualDropPosition === order
            ? topIndex
            : bottomIndex
    }

    // Non-Article NUGGETs
    if (order <= firstOrder || visualDropPosition === order) return topIndex
    if (order >= lastOrder) return bottomIndex

    return fallback
}

/**
 * Adjusts drop index to account for array shifts during reordering.
 * Dragging down: subtract 1 if dropping before. Dragging up: add 1 if dropping after.
 */
const adjustDropIndexForRemoval = (
    dragIndex: number,
    dropIndex: number,
    visualPosition: number
): number => {
    const isDraggingDown = dragIndex < dropIndex
    const isDroppingBefore = visualPosition < 0

    if (isDraggingDown) {
        return isDroppingBefore ? Math.max(0, dropIndex - 1) : dropIndex
    } else {
        const isDroppingAfter = visualPosition >= 0
        return isDroppingAfter ? dropIndex + 1 : dropIndex
    }
}

/** Collects descendants for reordered nodes so children move with parents. */
const collectDescendantsForReorderedNodes = (
    reorderedSiblings: Partial<ContentItemViewNodesType>[],
    allNodes: Partial<ContentItemViewNodesType>[]
): Partial<ContentItemViewNodesType>[] => {
    const parentIds = reorderedSiblings
        .map((s) => s.id)
        .filter((id): id is string => !!id)

    const descendants = getAllDescendantsOptimized(allNodes, parentIds)

    return [...reorderedSiblings, ...descendants]
}

export const useReorderItems = () => {
    const selectedKey = useSelectedKey()
    const { setSaveOperationInProgress } = useNodeMapActions()
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )
    const {
        contentItemViewNodes,
        actions: { setContentItemViewNodes },
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        articlesLoading: state.articlesLoading,
        actions: state.actions,
    }))
    const {
        mutateReorderFamilyTemplatesAsync,
        mutateReorderProductTypeTemplatesAsync,
    } = useHoveredActionMutationService()
    const { executeWithOptimistic } = useTransactionManager()
    const selectedMainMenuNode = useSelectedMainMenuNode()
    const { setIsMoveBlockMenuDisplayed } = useHoveredItemActions()
    const {
        shouldPreventDrag,
        validateDropPlacement,
        simulateAndValidate,
    } = useMOPTDragAndDropValidation()
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const { canEdit } = useEditingPermissions()
    const isDragging = useIsDragging()
    const { setIsDragging } = useDragAndDropActions()

    const handleDragStart = useCallback(() => {
        setIsDragging(true)
    }, [setIsDragging])

    const handleDragEnd = useCallback(() => setIsDragging(false), [
        setIsDragging,
    ])

    /**
     * Executes a reorder mutation with optimistic updates.
     * Handles success and error callbacks to update state accordingly.
     * @param reorderedSiblings - Array of reordered content item view nodes
     * @param apiPayload - Payload for the reorder API mutation
     * @param fallbackState - Serialized state to restore on error
     */
    const runReorderMutation = useCallback(
        (
            reorderedSiblings: Partial<ContentItemViewNodesType>[],
            apiPayload: any,
            fallbackState: string
        ) => {
            // Set save operation in progress before starting
            setSaveOperationInProgress(true)

            try {
                executeWithOptimistic({
                    fallbackState,
                    asyncAction: async () => {
                        if (
                            selectedProjectElementNode?.elementType ===
                            'PRODUCTFAMILY'
                        ) {
                            await mutateReorderFamilyTemplatesAsync?.(
                                apiPayload
                            )
                        } else {
                            await mutateReorderProductTypeTemplatesAsync?.(
                                apiPayload
                            )
                        }
                        return reorderedSiblings
                    },
                    successCbFn: () => {
                        // Include descendants when updating nodes to ensure children move with parents
                        const nodesToUpdate = collectDescendantsForReorderedNodes(
                            reorderedSiblings,
                            contentItemViewNodes
                        )

                        const updatedNodes = replaceNodesById(
                            contentItemViewNodes,
                            nodesToUpdate
                        )
                        setContentItemViewNodes(updatedNodes)
                        setSaveOperationInProgress(false)
                    },
                    errorCbFn: () => {
                        setContentItemViewNodes(contentItemViewNodes)
                        setSaveOperationInProgress(false)
                    },
                })
            } catch (error) {
                // Ensure save flag is cleared if executeWithOptimistic throws synchronously
                setSaveOperationInProgress(false)
            }
        },
        [
            executeWithOptimistic,
            selectedProjectElementNode,
            mutateReorderFamilyTemplatesAsync,
            mutateReorderProductTypeTemplatesAsync,
            contentItemViewNodes,
            setContentItemViewNodes,
            setSaveOperationInProgress,
        ]
    )

    /**
     * Handles the drop event for tree nodes.
     * This function manages the reordering of nodes within the same parent hierarchy
     * and ensures that the drag-and-drop operation adheres to the defined constraints.
     *
     * @param {Object} info - The information about the drop event.
     */
    const handleOnDrop = useCallback(
        (info: DragAndDropInfo) => {
            if (!canEdit) {
                return
            }

            // Prevent reordering while save operation is in progress
            if (isSaveOperationInProgress) {
                return
            }

            if (!selectedProjectElementNode?.isInProject) {
                return
            }

            const {
                dragKey,
                dropKey,
                dragNode,
                dropNode,
            } = getDragAndDropNodes(info, contentItemViewNodes)

            if (!dragNode || !dropNode) {
                return
            }

            // Validate drop: checks parent relationships, textType, and exceptions
            const dropValidation = isValidDrop(dropNode, dragNode)
            if (!dropValidation.isValid) {
                return
            }

            // Get the siblings of the dragged node within the same parent.
            const siblings = getSortedSiblings(
                contentItemViewNodes,
                dragNode.dtvParentId,
                dragNode.dtvContentItemParentId
            )

            // Find the indices of the dragged and drop nodes in the siblings array.
            const dragIndex = siblings.findIndex((node) => node.id === dragKey)
            let dropIndex = siblings.findIndex((node) => node.id === dropKey)

            if (dragIndex === -1) return

            const visualDropPosition =
                info.dropPosition ?? DEFAULT_DROP_POSITION

            // Handle special case: dropping onto boundary nodes (parent or NUGGET placeholders)
            if (dropIndex === -1) {
                dropIndex = resolveDropIndexForBoundaryNodes(
                    dropNode,
                    siblings,
                    visualDropPosition
                )
            } else {
                // Apply index adjustment logic for normal sibling drops
                dropIndex = adjustDropIndexForRemoval(
                    dragIndex,
                    dropIndex,
                    visualDropPosition
                )
            }

            // Prevent no-op moves
            if (dragIndex === dropIndex) {
                return
            }

            const { reorderedSiblings, apiPayload } = reorderSiblingContents(
                siblings,
                dragIndex,
                dropIndex,
                dragNode,
                selectedProjectElementNode
            )

            // MOPT Restrictions: Validate placement
            if (!validateDropPlacement(dragNode, reorderedSiblings)) {
                return
            }

            runReorderMutation(
                reorderedSiblings,
                apiPayload,
                JSON.stringify(contentItemViewNodes)
            )
            setIsDragging(false)
        },
        [
            canEdit,
            isSaveOperationInProgress,
            contentItemViewNodes,
            getSortedSiblings,
            executeWithOptimistic,
            setContentItemViewNodes,
            selectedProjectElementNode,
            validateDropPlacement,
            setIsDragging,
        ]
    )

    /**
     * Handles the drag-over event for tree nodes.
     * This function ensures that certain drag-and-drop operations are restricted
     * based on the type and hierarchy of the nodes involved.
     *
     * @param {DragAndDropInfo} info - The information about the drag-over event.
     */
    const handleOnDragOver = useCallback(
        (info: DragAndDropInfo) => {
            const { dragNode, dropNode, event } = getDragAndDropNodes(
                info,
                contentItemViewNodes
            )

            if (!dragNode || !dropNode) {
                return
            }

            // Prevent dropping an article node onto its own children.
            // This ensures that an article cannot be dropped into its descendants, maintaining a valid hierarchy.
            if (
                dragNode.type === NODE_TYPES.Article &&
                !dragNode.dtvParentId &&
                dropNode.dtvParentId === dragNode.id
            ) {
                event?.preventDefault()
                return
            }

            // Prevent dropping nodes outside of their current level.
            // This ensures that nodes remain within the same parent hierarchy.
            if (
                dragNode.type !== NODE_TYPES.Article &&
                dragNode.dtvParentId !== dropNode.dtvParentId
            ) {
                event?.preventDefault()
                return
            }

            // MOPT Restrictions: Prevent visual drop acceptance for invalid MOPT scenarios
            if (shouldPreventDrag(dragNode)) {
                event?.preventDefault()
                return
            }

            if (dragNode.textType === 'Article') {
                // Get siblings and calculate where the node will end up
                const siblings = getSortedSiblings(
                    contentItemViewNodes,
                    dragNode.dtvParentId,
                    dragNode.dtvContentItemParentId
                )

                const dragIndex = siblings.findIndex(
                    (node) => node.id === dragNode.id
                )
                let dropIndex = siblings.findIndex(
                    (node) => node.id === dropNode.id
                )

                if (dragIndex === -1) return

                // Prevent validation for no-op moves (hovering over itself)
                if (dragIndex === dropIndex) {
                    return
                }

                // Get drop position from the event
                const visualDropPosition =
                    info.dropPosition ?? DEFAULT_DROP_POSITION

                // Calculate the actual drop index considering boundary nodes
                if (dropIndex === -1) {
                    dropIndex = resolveDropIndexForBoundaryNodes(
                        dropNode,
                        siblings,
                        visualDropPosition
                    )
                } else {
                    dropIndex = adjustDropIndexForRemoval(
                        dragIndex,
                        dropIndex,
                        visualDropPosition
                    )
                }

                // Validate MOPT placement
                const { isValid } = simulateAndValidate(
                    dragNode,
                    siblings,
                    dragIndex,
                    dropIndex
                )

                if (!isValid) {
                    event?.preventDefault()
                    return
                }
            }
        },
        [contentItemViewNodes, shouldPreventDrag, simulateAndValidate]
    )

    /**
     * Handles moving a block (node) up or down within its parent hierarchy.
     * This function reorders the siblings of the hovered node based on the specified direction.
     * @param {'up' | 'down'} direction - The direction to move the block ('up' or 'down').
     */
    const handleMoveBlock = useCallback(
        (
            direction: 'up' | 'down',
            nodeArg?: Partial<ContentItemViewNodesType>
        ) => {
            if (!canEdit) {
                return
            }

            // Prevent reordering while save operation is in progress
            if (isSaveOperationInProgress) {
                return
            }

            if (!selectedProjectElementNode?.isInProject) {
                return
            }

            const nodeToMove = nodeArg ?? selectedMainMenuNode
            if (!nodeToMove) {
                return
            }

            // MOPT Restrictions: Prevent moving MOPT articles
            if (shouldPreventDrag(nodeToMove)) {
                return
            }

            const sortedSiblings = getSortedSiblings(
                contentItemViewNodes,
                nodeToMove.dtvParentId,
                nodeToMove.dtvContentItemParentId
            )

            const dragIndex = sortedSiblings.findIndex(
                (item) => item.id === nodeToMove.id
            )

            if (dragIndex === -1) return

            // Calculate target position for move direction
            const targetIndex =
                direction === 'up' ? dragIndex - 1 : dragIndex + 1

            // Prevent out-of-bounds access
            if (targetIndex < 0 || targetIndex >= sortedSiblings.length) return

            // Get the target node ID and find its index (like drag & drop does)
            const targetNode = sortedSiblings[targetIndex]
            if (!targetNode?.id) return

            const dropIndex = sortedSiblings.findIndex(
                (item) => item.id === targetNode.id
            )

            const { reorderedSiblings, apiPayload } = reorderSiblingContents(
                sortedSiblings,
                dragIndex,
                dropIndex,
                nodeToMove,
                selectedProjectElementNode
            )

            // MOPT Restrictions: Validate placement
            if (!validateDropPlacement(nodeToMove, reorderedSiblings)) {
                return
            }

            // Avoid unnecessary updates if the sibling order hasn't changed
            // Note: Compare against sortedSiblings (current sibling order) rather than
            // contentItemViewNodes (all nodes) to ensure accurate comparison
            if (
                JSON.stringify(reorderedSiblings) ===
                JSON.stringify(sortedSiblings)
            ) {
                return
            }

            runReorderMutation(
                reorderedSiblings,
                apiPayload,
                JSON.stringify(contentItemViewNodes)
            )
            setIsMoveBlockMenuDisplayed(false)
        },
        [
            canEdit,
            isSaveOperationInProgress,
            contentItemViewNodes,
            selectedMainMenuNode,
            getSortedSiblings,
            executeWithOptimistic,
            setContentItemViewNodes,
            selectedProjectElementNode,
            setIsMoveBlockMenuDisplayed,
            shouldPreventDrag,
            runReorderMutation,
        ]
    )

    const getReorderDirection = (
        e: KeyboardEvent | React.KeyboardEvent
    ): 'up' | 'down' | null => {
        if (tryGetShortcutKey('MoveUp', e)) return 'up'
        if (tryGetShortcutKey('MoveDown', e)) return 'down'

        return null
    }

    /**
     * Handles keyboard shortcuts for reordering items.
     *
     * @param {KeyboardEvent | React.KeyboardEvent} e - The keyboard event.
     */
    const handleKeyDown = useCallback(
        (e: KeyboardEvent | React.KeyboardEvent) => {
            const direction = getReorderDirection(e)
            if (!direction || !selectedKey) return

            e.preventDefault()

            const node = contentItemViewNodes.find((n) => n.id === selectedKey)
            if (!node || node.isHidden) return

            handleMoveBlock(direction, node)
        },
        [selectedKey, contentItemViewNodes, handleMoveBlock]
    )

    /**
     * Handles keydown events.
     * - Triggers block reorder shortcuts (MoveUp / MoveDown) even in editable fields.
     * - Allows normal typing otherwise.
     *
     * @param {React.KeyboardEvent} e - The keyboard event.
     */
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (getReorderDirection(e)) {
                handleKeyDown(e)
            }
        },
        [handleKeyDown]
    )

    /**
     * Determines if a drop operation should be allowed.
     * This prevents the Tree component from visually updating for invalid drops.
     * Called by Ant Design Tree's allowDrop prop.
     */
    const allowDrop = useCallback(
        (options: AllowDropOptions<BasicDataNode>) => {
            const { dragNode, dropNode, dropPosition } = options

            // Type assertion: Tree nodes always have keys in practice
            const dragKey = (dragNode as TreeNodeWithKey).key
            const dropKey = (dropNode as TreeNodeWithKey).key

            // Convert BasicDataNode to our ContentItemViewNodesType
            const dragNodeData = contentItemViewNodes.find(
                (n) => n.id === dragKey
            )
            const dropNodeData = contentItemViewNodes.find(
                (n) => n.id === dropKey
            )

            if (!dragNodeData || !dropNodeData) {
                return true // Allow if we can't find the nodes
            }

            // MOPT Restrictions: Prevent dragging MOPT articles
            if (shouldPreventDrag(dragNodeData)) {
                return false
            }

            // MOPT Restrictions: Validate placement for non-MOPT articles
            if (dragNodeData.textType === 'Article') {
                // Get siblings and calculate where the node will end up
                const siblings = getSortedSiblings(
                    contentItemViewNodes,
                    dragNodeData.dtvParentId,
                    dragNodeData.dtvContentItemParentId
                )

                const dragIndex = siblings.findIndex(
                    (node) => node.id === dragNodeData.id
                )
                let dropIndex = siblings.findIndex(
                    (node) => node.id === dropNodeData.id
                )

                if (dragIndex === -1) return true

                // Prevent validation for no-op moves (hovering over itself)
                if (dragIndex === dropIndex) {
                    return true
                }

                // Calculate the actual drop index
                if (dropIndex === -1) {
                    dropIndex = resolveDropIndexForBoundaryNodes(
                        dropNodeData,
                        siblings,
                        dropPosition
                    )
                } else {
                    dropIndex = adjustDropIndexForRemoval(
                        dragIndex,
                        dropIndex,
                        dropPosition
                    )
                }

                // Validate MOPT placement
                const { isValid } = simulateAndValidate(
                    dragNodeData,
                    siblings,
                    dragIndex,
                    dropIndex
                )

                if (!isValid) {
                    return false
                }
            }

            return true // Allow drop
        },
        [contentItemViewNodes, shouldPreventDrag, simulateAndValidate]
    )

    return {
        handleOnDrop,
        handleOnDragOver,
        handleMoveBlock,
        isDragging,
        handleDragStart,
        handleDragEnd,
        handleKeyDown,
        onKeyDown,
        allowDrop,
    }
}
